import cx from 'classnames';
import React, {forwardRef, useEffect, useRef} from 'react';
import type SimpleBarCore from 'simplebar-core';
import SimpleBar, {type Props} from 'simplebar-react';

import {TestId} from '../../../utils/testIds';

import styles from './Scrollbars.module.scss';

// FIXME: Optional: implement `AutoHideBehavior` as it was in https://kingsora.github.io/OverlayScrollbars/#!documentation/options
export type AutoHideBehavior = 'never' | 'scroll' | 'leave' | 'move';

export const Scrollbars = forwardRef<
    SimpleBarCore,
    Omit<Props, 'autoHide'> & {
        autoHide?: AutoHideBehavior;
        scrollElementRef?: React.MutableRefObject<HTMLElement | null>;
        onScroll?: (e: Event) => void;
        className?: string;
        scrollElementTabIndex?: -1 | 0;
    }
>(
    (
        {
            autoHide,
            children,
            className,
            onScroll,
            scrollElementRef,
            scrollElementTabIndex = 0,
            ...props
        },
        ref,
    ) => {
        const localRef = useRef<HTMLElement | null>(null);

        useEffect(() => {
            const localRefCopy = localRef;

            const scroll = (e: Event) => {
                onScroll?.(e);
            };

            localRefCopy.current?.addEventListener('scroll', scroll);

            return () =>
                localRefCopy.current?.removeEventListener('scroll', scroll);
        }, [onScroll]);

        return (
            <SimpleBar
                className={cx(styles.scrollbars, className)}
                ref={ref}
                scrollableNodeProps={{
                    ref: (element: HTMLElement | null) => {
                        if (element) {
                            element.tabIndex = scrollElementTabIndex;
                        }
                        localRef.current = element;
                        if (scrollElementRef) {
                            scrollElementRef.current = element;
                        }
                    },
                    ['data-testid']: TestId.SimpleBarScrollable,
                }}
                autoHide={autoHide !== 'never'}
                {...props}
            >
                {children}
            </SimpleBar>
        );
    },
);

Scrollbars.displayName = 'Scrollbars';

type IScrollbars = SimpleBarCore | null;

export type {IScrollbars};
